import React, { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaFacebook } from "react-icons/fa";
import { FaTiktok } from "react-icons/fa6";
import CardsCarousel from "./CardsCarousel";
import { getDataFacebookWithPrev, getDataTiktokWithPrev } from "../../../../redux/apiRequest";
import { useNavigate } from "react-router-dom";
import { createAxios } from "../../../../createInstance";
import { loginSuccess } from "../../../../redux/authSlice";

export default function SocialSection({ period, curRange }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.login.currentUser);
  const axiosJWT = useMemo(
    () => (user ? createAxios(user, dispatch, loginSuccess, navigate) : null),
    [user, dispatch, navigate]
  );
  const slug = useMemo(() => {
    const c = user?.customer;
    return typeof c === "string" ? c : c?.slug;
  }, [user]);

  const fbState = useSelector((state) => state.facebook.facebooks?.listData);
  const tkState = useSelector((state) => state.tiktok.tiktoks?.listData);
  const lastFetchKeyRef = useRef("");
  useEffect(() => {
    if (!slug || !axiosJWT || !curRange) return;
    const key = `${slug}|fb:${curRange?.fb?.since}-${curRange?.fb?.until}|tk:${curRange?.tk?.since}-${curRange?.tk?.until}`;
    if (lastFetchKeyRef.current === key) return;
    lastFetchKeyRef.current = key;
    try {
      getDataFacebookWithPrev(slug, user?.accessToken, dispatch, axiosJWT, curRange.fb);
      getDataTiktokWithPrev(slug, user?.accessToken, dispatch, axiosJWT, curRange.tk);
    } catch {}
  }, [slug, user, axiosJWT, curRange, dispatch]);

  const socialItems = useMemo(() => {
    const items = [];

    const sumMetrics = (rows = []) => {
      const sums = {};
      for (const r of rows || []) {
        Object.keys(r || {}).forEach((k) => {
          if (k === 'day' || k === 'date' || k === 'id' || k === 'name') return;
          const v = Number(r[k]);
          if (!Number.isFinite(v)) return;
          sums[k] = (sums[k] || 0) + v;
        });
      }
      return sums;
    };
    const findPrevPage = (curPage) => {
      const prevPages = fbState?.previous?.pages || [];
      const id = curPage?.pageId || curPage?.id || curPage?.page_id;
      const name = curPage?.pageName || curPage?.name || curPage?.page_name;
      return (
        prevPages.find(
          (p) =>
            (id && (p?.pageId === id || p?.id === id || p?.page_id === id)) ||
            (name && (p?.pageName === name || p?.name === name || p?.page_name === name))
        ) || null
      );
    };

    const fbPages = fbState?.current?.pages || [];
    fbPages.forEach((p) => {
      const cur = sumMetrics(p?.data || []);
      const prev = sumMetrics((findPrevPage(p)?.data) || []);

      const fbKeyOrder = [
        'page_impressions',
        'page_impressions_unique',
        'page_post_engagements',
        'page_daily_follows',
        'page_impressions_organic_unique',
        'page_impressions_paid_unique',
        'page_views_total',
        'page_video_views',
      ];
      const fbLabels = {
        page_impressions: 'Lượt hiển thị',
        page_impressions_unique: 'Tiếp cận (unique)',
        page_post_engagements: 'Lượt tương tác',
        page_daily_follows: 'Follower (tăng/giảm)',
        page_impressions_organic_unique: 'Lượt tiếp cận (tự nhiên)',
        page_impressions_paid_unique: 'Lượt tiếp cận (quảng cáo)',
        page_views_total: 'Lượt truy cập',
        page_video_views: 'Lượt xem video ≥ 3s',
      };

      const otherKeys = Object.keys(cur)
        .filter(k => !fbKeyOrder.includes(k))
        .sort();
      const displayKeys = [...fbKeyOrder, ...otherKeys]
        .filter(k => cur[k] != null || prev[k] != null)
        .filter(k => k !== 'page_impressions_organic_unique' && k !== 'page_impressions_paid_unique');

      const rows = displayKeys.map((k) => {
        const c = Number(cur[k] || 0);
        const pval = Number(prev[k] || 0);
        const diff = c - pval;
        const pctVal = pval > 0 ? Math.round((diff / pval) * 100) : null;
        const pct = pctVal === null ? '-' : `${pctVal}%`;
        if (k === 'page_impressions_unique') {
          const org = Number(cur['page_impressions_organic_unique'] || 0);
          const paid = Number(cur['page_impressions_paid_unique'] || 0);
          return {
            key: k,
            combined: true,
            label: fbLabels[k] || k.replace(/_/g, ' '),
            value: c.toLocaleString('vi-VN'),
            orgLabel: 'tự nhiên',
            orgValue: org.toLocaleString('vi-VN'),
            orgDir: (org - Number(prev['page_impressions_organic_unique'] || 0)) >= 0 ? 'up' : 'down',
            paidLabel: 'quảng cáo',
            paidValue: paid.toLocaleString('vi-VN'),
            paidDir: (paid - Number(prev['page_impressions_paid_unique'] || 0)) >= 0 ? 'up' : 'down',
            delta: Math.abs(diff).toLocaleString('vi-VN'),
            pct,
            dir: diff >= 0 ? 'up' : 'down',
          };
        }
        return {
          key: k,
          label: fbLabels[k] || k.replace(/_/g, ' '),
          value: c.toLocaleString('vi-VN'),
          delta: Math.abs(diff).toLocaleString('vi-VN'),
          pct,
          dir: diff >= 0 ? 'up' : 'down',
        };
      });

      const title = `${p?.pageName || p?.name || p?.page_name || 'Page'}`;
      const series = (p?.data || []).map((r) => {
        const d = new Date(String(r.day || r.date || ''));
        const label = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
        const impressions = Number(r.page_impressions ?? 0);
        const reach = Number(r.page_impressions_unique ?? 0);
        return { date: label, impressions, reach };
      });

      items.push({
        icon: <FaFacebook color="#1877F2" />,
        title,
        rows,
        type: 'facebook',
        chart: series,
        chartLabels: {
          impressions: 'Lượt hiển thị',
          reach: 'Lượt tiếp cận',
        },
      });
    });

    const curList = Array.isArray(tkState?.current) ? tkState.current : (tkState?.current || []);
    const prevList = Array.isArray(tkState?.previous) ? tkState.previous : (tkState?.previous || []);
    const matchPrev = (it) => {
      if (it?.openId) {
        const byOpenId = prevList.find(p => p?.openId === it.openId);
        if (byOpenId) return byOpenId;
      }
      const id = it?.displayName || it?.username || it?.uniqueId || it?.id || it?.uid;
      return prevList.find(p => (p?.displayName || p?.username || p?.uniqueId || p?.id || p?.uid) === id) || null;
    };
    curList.forEach((it) => {
      const prvIt = matchPrev(it) || {};
      const curDiff = it?.diff || {};
      const prevDiff = prvIt?.diff || {};

      const tkKeyOrder = ['totalViews', 'likesCount', 'followerCount', 'followingCount', 'videoCount'];
      const tkLabels = {
        totalViews: 'Lượt hiển thị',
        likesCount: 'Lượt thích',
        commentsCount: 'Bình luận',
        sharesCount: 'Chia sẻ',
        followersCount: 'Follower',
      };
      const numberKeys = Array.from(new Set([
        ...Object.keys(curDiff || {}),
        ...Object.keys(prevDiff || {}),
      ])).filter(k => Number.isFinite(Number(curDiff[k])) || Number.isFinite(Number(prevDiff[k])));
      const otherKeys = numberKeys.filter(k => !tkKeyOrder.includes(k)).sort();
      const displayKeys = [...tkKeyOrder, ...otherKeys].filter(k => (curDiff[k] != null || prevDiff[k] != null));
      const tkLabels2 = {
        totalViews: 'Lượt hiển thị',
        likesCount: 'Lượt thích',
        followerCount: 'Follower',
        followingCount: 'Đang theo dõi',
        videoCount: 'Số video',
      };

      const rows = displayKeys.map((k) => {
        const c = Number(curDiff[k] || 0);
        const pval = Number(prevDiff[k] || 0);
        const diff = c - pval;
        const pctVal = pval > 0 ? Math.round((diff / pval) * 100) : null;
        const pct = pctVal === null ? '-' : `${pctVal}%`;
        return {
          key: k,
          label: (tkLabels2[k] ?? tkLabels[k] ?? k),
          value: c.toLocaleString('vi-VN'),
          delta: Math.abs(diff).toLocaleString('vi-VN'),
          pct,
          dir: diff >= 0 ? 'up' : 'down',
        };
      });

      const name = it?.displayName || it?.username || it?.uniqueId || it?.id || 'Account';
      const title = `TikTok: ${name}`;
      const seriesSrc = Array.isArray(it?.series) ? it.series : [];
      const series = seriesSrc.map((s) => {
        const parts = String(s?.date || "").split("/");
        const label = parts.length >= 2 ? `${parts[0]}/${parts[1]}` : (s?.date || "");
        const impressions = Number(s?.totalViews ?? s?.views ?? 0);
        const reachSource =
          s?.likesCount ??
          s?.likes ??
          s?.likeCount ??
          s?.hearts ??
          it?.diff?.likesCount ??
          0;
        return { date: label, impressions, reach: Number(reachSource) };
      });
      items.push({
        icon: <FaTiktok color="#000000" />,
        title,
        rows,
        type: 'tiktok',
        chart: series,
        chartLabels: {
          impressions: 'Lượt hiển thị',
          reach: 'Lượt thích',
        },
      });
    });
    return items;
  }, [fbState, tkState]);

  return <CardsCarousel title="Social Media" items={socialItems} period={period} />;
}
